<!--
	Marquee — the readout-voice ticker strip. Pure CSS: the animation runs with no
	JS at all, and the global prefers-reduced-motion rule parks it on a static
	frame. The track is duplicated so the loop is seamless; the copy is aria-hidden.
	One per page — a ticker that repeats is a slot machine.
-->
<script lang="ts">
	let {
		items,
		class: klass = ''
	}: {
		items: readonly string[];
		class?: string;
	} = $props();
</script>

{#snippet track()}
	<!-- Both tracks aria-hidden: the container's role/label carries the text, so
	     screen readers hear it once instead of reading a loop. -->
	<div class="marquee__track" aria-hidden="true">
		<!-- Repeated so a short item list still overflows wide viewports and the
		     loop never shows a gap. -->
		{#each [0, 1, 2] as r (r)}
			{#each items as item, i (i)}
				<span class="marquee__item">{item}</span>
			{/each}
		{/each}
	</div>
{/snippet}

<div class="marquee {klass}" role="note" aria-label={items.join(' · ')}>
	{@render track()}
	{@render track()}
</div>

<style>
	.marquee {
		display: flex;
		overflow: hidden;
		border-block: 1px solid var(--color-line);
		background-color: var(--color-surface);
		padding-block: 0.625rem;
		/* Fade the ends so items don't clip hard against the viewport edge. */
		-webkit-mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);
		mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);
	}
	.marquee__track {
		display: flex;
		flex: none;
		align-items: center;
		min-width: 100%;
		animation: marquee 36s linear infinite;
	}
	.marquee:hover .marquee__track {
		animation-play-state: paused;
	}
	.marquee__item {
		display: inline-flex;
		align-items: center;
		gap: 1.75rem;
		padding-right: 1.75rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		/* ink-500, not ink-400: the strip sits on pure white --color-surface,
		   where ink-400 dips under the 4.5:1 AA line. */
		color: var(--color-ink-500);
		white-space: nowrap;
	}
	/* The cut tick between items — the same 6px square the eyebrows use. */
	.marquee__item::after {
		content: '';
		width: 6px;
		height: 6px;
		background-color: var(--color-cut-600);
	}
	@keyframes marquee {
		to {
			transform: translateX(-100%);
		}
	}
</style>
